import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Chauffeur, Camion } from '../../types';
import weighingService from '../../services/weighing';

interface EditDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Chauffeur>) => void;
  driver: Chauffeur;
}

const EditDriverModal: React.FC<EditDriverModalProps> = ({
  isOpen,
  onClose,
  onSave,
  driver
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    permis: '',
    telephone: '',
    email: '',
    nombre_livraisons: 0,
    camions: [] as number[]
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [vehicles, setVehicles] = useState<Camion[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState('');
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);

  useEffect(() => {
    if (driver) {
      setFormData({
        nom: driver.nom || '',
        prenom: driver.prenom || '',
        permis: driver.permis || '',
        telephone: driver.telephone || '',
        email: driver.email || '',
        nombre_livraisons: driver.nombre_livraisons || 0,
        camions: driver.camions || []
      });
      setErrors({});
    }
  }, [driver]);

  // Charger la liste des véhicules disponibles
  useEffect(() => {
    if (isOpen) {
      loadVehicles();
    }
  }, [isOpen]);

  const loadVehicles = async () => {
    setLoadingVehicles(true);
    try {
      const response = await weighingService.getCamions();
      if (response && response.results) {
        setVehicles(response.results || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des véhicules:', error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'nombre_livraisons' ? parseInt(value) || 0 : value
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleVehicleToggle = (vehicleId: number) => {
    setFormData(prev => ({
      ...prev,
      camions: prev.camions.includes(vehicleId)
        ? prev.camions.filter(id => id !== vehicleId)
        : [...prev.camions, vehicleId]
    }));
  };

  const handleRemoveVehicle = (vehicleId: number) => {
    setFormData(prev => ({
      ...prev,
      camions: prev.camions.filter(id => id !== vehicleId)
    }));
  };

  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.immatriculation.toLowerCase().includes(vehicleSearchTerm.toLowerCase()) ||
    vehicle.marque?.toLowerCase().includes(vehicleSearchTerm.toLowerCase()) ||
    vehicle.modele?.toLowerCase().includes(vehicleSearchTerm.toLowerCase())
  );

  const selectedVehicles = vehicles.filter(vehicle => formData.camions.includes(vehicle.id));

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }

    // Les champs permis, telephone et email sont maintenant optionnels

    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifier le chauffeur" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nom et Prénom */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom du chauffeur"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prénom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.prenom}
              onChange={(e) => handleInputChange('prenom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.prenom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Prénom du chauffeur"
            />
            {errors.prenom && (
              <p className="mt-1 text-sm text-red-600">{errors.prenom}</p>
            )}
          </div>
        </div>

        {/* Permis et Téléphone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permis
            </label>
            <select
              value={formData.permis}
              onChange={(e) => handleInputChange('permis', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.permis ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un permis</option>
              <option value="A">Permis A (Moto/Tricycle)</option>
              <option value="B">Permis B (Voiture légère)</option>
              <option value="C">Permis C (Camion/marchandises)</option>
              <option value="D">Permis D (Transport en commun/bus)</option>
              <option value="E">Permis E (Ensembles articulés/véhicules lourds)</option>
              <option value="F">Permis F (Personnes handicapées)</option>
              <option value="BCDE">Permis BCDE</option>
              <option value="ABCDE">Permis ABCDE</option>
              <option value="ABCDEF">Permis ABCDEF</option>
            </select>
            {errors.permis && (
              <p className="mt-1 text-sm text-red-600">{errors.permis}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => handleInputChange('telephone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.telephone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="01 23 45 67 89"
            />
            {errors.telephone && (
              <p className="mt-1 text-sm text-red-600">{errors.telephone}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            placeholder="chauffeur@email.com"
          />
        </div>

        {/* Nombre de livraisons */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre de livraisons
          </label>
          <input
            type="number"
            value={formData.nombre_livraisons}
            onChange={(e) => handleInputChange('nombre_livraisons', parseInt(e.target.value) || 0)}
            min="0"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.nombre_livraisons ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="0"
          />
          {errors.nombre_livraisons && (
            <p className="mt-1 text-sm text-red-600">{errors.nombre_livraisons}</p>
          )}
        </div>

        {/* Véhicules associés */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Véhicules que ce chauffeur peut conduire
          </label>
          
          {/* Véhicules sélectionnés */}
          {selectedVehicles.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                {selectedVehicles.map((vehicle) => (
                  <span
                    key={vehicle.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                  >
                    {vehicle.immatriculation}
                    <button
                      type="button"
                      onClick={() => handleRemoveVehicle(vehicle.id)}
                      className="ml-2 text-orange-600 hover:text-orange-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bouton pour ouvrir le sélecteur */}
          <button
            type="button"
            onClick={() => setShowVehicleSelector(!showVehicleSelector)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            {showVehicleSelector ? 'Masquer la liste des véhicules' : 'Sélectionner des véhicules'}
            <span className="float-right text-gray-400">
              {showVehicleSelector ? '▲' : '▼'}
            </span>
          </button>

          {/* Sélecteur de véhicules */}
          {showVehicleSelector && (
            <div className="mt-3 border border-gray-300 rounded-lg">
              {/* Barre de recherche */}
              <div className="p-3 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Rechercher un véhicule (immatriculation, marque, modèle)..."
                  value={vehicleSearchTerm}
                  onChange={(e) => setVehicleSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              {/* Liste des véhicules */}
              <div className="max-h-60 overflow-y-auto">
                {loadingVehicles ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto"></div>
                    <span className="text-sm text-gray-500">Chargement des véhicules...</span>
                  </div>
                ) : filteredVehicles.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    {vehicleSearchTerm ? 'Aucun véhicule trouvé' : 'Aucun véhicule disponible'}
                  </p>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredVehicles.map((vehicle) => (
                      <label
                        key={vehicle.id}
                        className={`flex items-center space-x-3 p-3 cursor-pointer hover:bg-gray-50 ${
                          formData.camions.includes(vehicle.id) ? 'bg-orange-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.camions.includes(vehicle.id)}
                          onChange={() => handleVehicleToggle(vehicle.id)}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {vehicle.immatriculation}
                          </div>
                          <div className="text-sm text-gray-500">
                            {[vehicle.marque, vehicle.modele].filter(Boolean).join(' ') || 'N/A'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <p className="mt-2 text-sm text-gray-500">
            {selectedVehicles.length} véhicule{selectedVehicles.length > 1 ? 's' : ''} sélectionné{selectedVehicles.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Mettre à jour
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditDriverModal;
